import React, { useState, useEffect, useCallback, useRef } from 'react';
import ModuleTemplate from '../../ModuleTemplate';
import { useSpeakAnswer } from '../../lib/useSpeakAnswer';
import { useInstructionAudio } from '../../lib/useInstructionAudio';
import { fallbackWrongKey } from '../../lib/speechAnswerEvaluator';
import './SoundPosition.css';

const POSITIONS = ['начало', 'середина', 'конец'];

/* eslint-disable react-hooks/exhaustive-deps */

const S3_BASE_URL = 'https://7cdc3d1b-1cdd-4f85-aaa4-fa5c412f8f13.selstorage.ru/vocabulary';

const EXERCISE_DATA = [
  { sound: 'Р', word: 'рыбы', position: 'начало', image: `${S3_BASE_URL}/fishes.png`, consonantCluster: false, stressed: false },
  { sound: 'Р', word: 'корова', position: 'середина', image: `${S3_BASE_URL}/cow.png`, consonantCluster: false, stressed: false },
  { sound: 'Р', word: 'тигр', position: 'конец', image: `${S3_BASE_URL}/tiger.png`, consonantCluster: true, stressed: false },
  { sound: 'М', word: 'мяч', position: 'начало', image: `${S3_BASE_URL}/ball.png`, consonantCluster: false, stressed: true },
  { sound: 'М', word: 'лампа', position: 'середина', image: `${S3_BASE_URL}/lamp.png`, consonantCluster: false, stressed: true },
  { sound: 'М', word: 'дом', position: 'конец', image: `${S3_BASE_URL}/house.png`, consonantCluster: false, stressed: true },
  { sound: 'П', word: 'палка', position: 'начало', image: `${S3_BASE_URL}/stick.png`, consonantCluster: false, stressed: true },
  { sound: 'П', word: 'лампа', position: 'середина', image: `${S3_BASE_URL}/lamp.png`, consonantCluster: false, stressed: false },
  { sound: 'П', word: 'кетчуп', position: 'конец', image: `${S3_BASE_URL}/ketchup.png`, consonantCluster: false, stressed: false },
  { sound: 'Л', word: 'лампа', position: 'начало', image: `${S3_BASE_URL}/lamp.png`, consonantCluster: false, stressed: true },
  { sound: 'Л', word: 'вилка', position: 'середина', image: `${S3_BASE_URL}/fork.png`, consonantCluster: true, stressed: false },
  { sound: 'Л', word: 'стул', position: 'конец', image: `${S3_BASE_URL}/chair.png`, consonantCluster: false, stressed: true },
  { sound: 'С', word: 'сова', position: 'начало', image: `${S3_BASE_URL}/owl.png`, consonantCluster: false, stressed: false },
  { sound: 'С', word: 'коса', position: 'середина', image: `${S3_BASE_URL}/braid.png`, consonantCluster: false, stressed: false },
  { sound: 'С', word: 'автобус', position: 'конец', image: `${S3_BASE_URL}/bus.png`, consonantCluster: false, stressed: false },
  { sound: 'Ш', word: 'шапка', position: 'начало', image: `${S3_BASE_URL}/hat.png`, consonantCluster: false, stressed: true },
  { sound: 'Ш', word: 'кошка', position: 'середина', image: `${S3_BASE_URL}/cat.png`, consonantCluster: false, stressed: false },
  { sound: 'Ш', word: 'мышь', position: 'конец', image: `${S3_BASE_URL}/mouse.png`, consonantCluster: false, stressed: true },
  { sound: 'Т', word: 'тыква', position: 'начало', image: `${S3_BASE_URL}/pumpkin.png`, consonantCluster: false, stressed: false },
  { sound: 'Т', word: 'вата', position: 'середина', image: `${S3_BASE_URL}/vata.png`, consonantCluster: false, stressed: true },
  { sound: 'Т', word: 'бант', position: 'конец', image: `${S3_BASE_URL}/bow.png`, consonantCluster: true, stressed: true },
];

const SoundPosition = ({ onBack, onComplete }) => {
  const { start: startSpeakRecognition, stop: stopSpeakRecognition } = useSpeakAnswer('SoundPosition');
  const { play: playInstruction, stop: stopInstruction } = useInstructionAudio();
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [answerLog, setAnswerLog] = useState([]);
  const [showResult, setShowResult] = useState(false);
  const [selectedPos, setSelectedPos] = useState(null);
  const [isCorrect, setIsCorrect] = useState(null);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [recognizerReady, setRecognizerReady] = useState(false);
  // speakInstruction is memoized on [currentQuestion] via useCallback, so its
  // nested startListening -> handleSelect closure chain freezes at the render
  // right before the previous answer's setSelectedPos(null) reset actually
  // lands. Reading selectedPos state directly in that frozen closure sees
  // the *previous* answer, not null — silently swallowing every voice
  // answer from the second question onward. A ref sidesteps closure
  // staleness entirely since it's a shared mutable box, not a per-render
  // snapshot.
  const selectedPosRef = useRef(null);

  useEffect(() => {
    const shuffled = [...EXERCISE_DATA].sort(() => Math.random() - 0.5);
    setQuestions(shuffled);
  }, []);

  const currentQuestion = questions[currentIndex];
  const totalQuestions = questions.length;

  const startListening = () => {
    setRecognizerReady(false);
    startSpeakRecognition(
      POSITIONS.map((pos) => ({ key: pos, text: pos })),
      {
        onListening: () => setRecognizerReady(true),
        onResult: ({ matchedKey }) => {
          // A timeout or a mishearing (common with a small offline model)
          // used to just leave the question hanging with no reaction at
          // all — resolve to some position either way so voice answers are
          // never a dead end; the clickable rects remain available too.
          const resolvedKey = matchedKey || fallbackWrongKey(
            POSITIONS.map((p) => ({ key: p })),
            currentQuestion.position
          );
          handleSelect(resolvedKey);
        },
      }
    );
  };

  // Функция озвучки (и перезапуска по нажати на "Повторить")
  const speakInstruction = useCallback(() => {
    if (!currentQuestion) return;
    stopInstruction();
    stopSpeakRecognition();
    setIsSpeaking(true);
    setIsListening(true);
    setRecognizerReady(false);
    setSelectedPos(null);
    selectedPosRef.current = null;
    setIsCorrect(null);

    playInstruction(currentQuestion.word, () => {
      setIsListening(false);
      setTimeout(() => {
        playInstruction(`Где ты слышишь звук "${currentQuestion.sound}"?`, () => {
          setIsSpeaking(false);
          startListening();
        });
      }, 600);
    });
  }, [currentQuestion]);

  useEffect(() => {
    if (currentQuestion && !showResult) {
      speakInstruction();
    }
  }, [currentQuestion, showResult]);

  const handleSelect = (pos) => {
    if (selectedPosRef.current !== null) return;
    selectedPosRef.current = pos;
    stopInstruction();
    stopSpeakRecognition();

    setSelectedPos(pos);
    const isAnswerCorrect = pos === currentQuestion.position;
    setIsCorrect(isAnswerCorrect);
    setAnswers([...answers, isAnswerCorrect]);
    setAnswerLog(prev => [...prev, {
      word: currentQuestion.word,
      sound: currentQuestion.sound,
      position: currentQuestion.position,
      chosenPosition: pos,
      consonantCluster: currentQuestion.consonantCluster,
      stressed: currentQuestion.stressed,
      correct: isAnswerCorrect,
    }]);

    setTimeout(() => {
      if (currentIndex < totalQuestions - 1) {
        setCurrentIndex(currentIndex + 1);
      } else {
        setShowResult(true);
      }
    }, 1500);
  };

  useEffect(() => {
    if (showResult) {
      const timer = setTimeout(() => {
        const correctCount = answers.filter(a => a === true).length;
        const score = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;

        const failedPositions = [...new Set(answerLog.filter(a => !a.correct).map(a => a.position))];
        const clusterErrors = answerLog.filter(a => !a.correct && a.consonantCluster).length;
        const unstressedErrors = answerLog.filter(a => !a.correct && !a.stressed).length;
        const failedItems = answerLog
          .filter(a => !a.correct)
          .map(a => ({ word: a.word, sound: a.sound, expectedPosition: a.position, chosenPosition: a.chosenPosition }));
        const correctItems = answerLog
          .filter(a => a.correct)
          .map(a => ({ word: a.word, sound: a.sound, position: a.position }));

        if (onComplete) {
          onComplete({
            testId: 'SoundPosition',
            score,
            details: { total: totalQuestions, correct: correctCount, failedPositions, clusterErrors, unstressedErrors, failedItems, correctItems },
          });
        } else {
          onBack();
        }
      }, 4000);
      return () => clearTimeout(timer);
    }
  }, [showResult]);

  if (showResult) {
    const correctCount = answers.filter(a => a === true).length;
    return (
      <ModuleTemplate 
        progress={answers} 
        totalQuestions={totalQuestions} 
        forceStop={true}
        onComplete={onComplete || onBack}
      >
        <div className="result-content">
          <h2 className="result-title">🎉 Упражнение завершено!</h2>
          <div className="result-score">
            <span className="score-number">{correctCount}</span>
            <span className="result-total"> / {totalQuestions}</span>
          </div>
        </div>
      </ModuleTemplate>
    );
  }

  if (!currentQuestion) return (
    <ModuleTemplate 
      progress={[]} 
      totalQuestions={1} 
      forceStop={true}
      onComplete={onComplete || onBack}
    >
      <div className="loading-text">Загрузка...</div>
    </ModuleTemplate>
  );

  return (
    <ModuleTemplate
      progress={answers}
      totalQuestions={totalQuestions}
      forceStop={true}
      onComplete={onComplete || onBack}
      onRepeat={speakInstruction}
      disabled={selectedPos !== null}
      extraContent={`Где звук "${currentQuestion.sound}"?`}
    >
      <div className="sound-position-layout">
        <div className="sound-position-left">
          {POSITIONS.map((pos) => {
            const isSelected = selectedPos === pos;
            const showCorrect = isSelected && isCorrect === true;
            const showWrong = isSelected && isCorrect === false;

            return (
              <button
                key={pos}
                onClick={() => handleSelect(pos)}
                disabled={selectedPos !== null}
                className={`sound-pos-rect ${showCorrect ? 'correct' : ''} ${showWrong ? 'wrong' : ''}`}
              >
                <span className="rect-dot"></span>
              </button>
            );
          })}
        </div>

        <div className="sound-position-center">
          <div className="sound-position-image-wrapper">
            <img
              src={currentQuestion.image}
              alt={currentQuestion.word}
              className="position-card-image"
              onError={(e) => { e.target.src = '/placeholder.png'; }}
            />
          </div>
        </div>

        <div className="sound-position-right">
          <div className={`indicator listen ${isListening ? 'active' : ''}`}>
            <span className="indicator-icon">🎧</span>
          </div>
          <div className={`indicator speak ${recognizerReady && !isSpeaking && selectedPos === null ? 'active' : ''}`}>
            <span className="indicator-icon">🗣️</span>
          </div>
        </div>
      </div>
    </ModuleTemplate>
  );
};

export default SoundPosition;